import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { FormHelperText, Grid, Divider, Stack, Skeleton, MenuItem } from '@mui/material';
import { Box } from '@mui/system';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { styles } from './styles';
import TextField from '../../../components/common/InputField/TextField';
import { useBulkNewDepartmentActionMutation, useModifyDepartmentActionMutation } from '../../../api/departments';
import DepartmentFields from '../DepartmentFields';
import { mapBulkApiErrorsToFields } from '../../../utils/map-bulk-api-errors';

const nameSchema = Yup.string()
    .max(255)
    .required('Name is required')
    .matches(
        /^[A-Za-z0-9&(),\-\s]+$/,
        'Name can contain only letters, numbers, spaces, ampersand (&), parentheses, commas, and hyphens',
    );

const statusSchema = Yup.string().max(255).required('Status is required');

const departmentItemSchema = Yup.object().shape({
    name: nameSchema,
    status: statusSchema,
});

const DepartmentDetailsTab = ({ isEditMode, departmentDetails, isLoading }) => {
    const navigate = useNavigate();
    const { status = [], results = {} } = departmentDetails || {};
    const { enqueueSnackbar } = useSnackbar();
    const [bulkNewDepartmentAction, { isLoading: isBulkNewLoading }] = useBulkNewDepartmentActionMutation();
    const [modifyDepartmentAction, { isLoading: isModifyLoading }] = useModifyDepartmentActionMutation();
    const [serverFieldErrors, setServerFieldErrors] = useState([]);

    const loading = isBulkNewLoading || isModifyLoading;

    const applyServerFieldErrors = (departments, apiErrors) => {
        const fieldErrors = mapBulkApiErrorsToFields(departments, apiErrors);
        setServerFieldErrors(fieldErrors);
    };

    const handleClearServerError = (index) => {
        setServerFieldErrors((prev) => {
            const updated = [...prev];
            updated[index] = {};
            return updated;
        });
    };

    const handleCreateDepartments = async (departments) => {
        try {
            const response = await bulkNewDepartmentAction({
                departments: departments.map((department) => ({
                    name: department.name,
                    status: department.status,
                })),
            }).unwrap();

            if (!response) return;

            const apiErrors = response?.errors || {};
            const hasFieldErrors = Object.keys(apiErrors).length > 0;

            if (hasFieldErrors) {
                applyServerFieldErrors(departments, apiErrors);
                if (response?.message) {
                    enqueueSnackbar(response.message, { variant: 'error' });
                }
                return;
            }

            if (response?.status === 'ERROR') {
                enqueueSnackbar(response?.message || 'Something went wrong!', { variant: 'error' });
                return;
            }

            setServerFieldErrors([]);
            enqueueSnackbar(
                response?.message ||
                    (departments.length === 1
                        ? 'Department created successfully.'
                        : `${departments.length} departments created successfully.`),
                { variant: 'success' },
            );
            navigate('/settings/departments');
        } catch (err) {
            const apiErrors = err?.data?.errors;
            if (apiErrors && Object.keys(apiErrors).length > 0) {
                applyServerFieldErrors(departments, apiErrors);
                if (err?.data?.message) {
                    enqueueSnackbar(err.data.message, { variant: 'error' });
                }
                return;
            }
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    const handleModifyDepartment = async (details) => {
        try {
            const bodyFormData = new FormData();
            for (const key in details) {
                bodyFormData.append(key, details[key]);
            }
            const response = await modifyDepartmentAction(bodyFormData).unwrap();
            if (!response) return;
            if (response?.status === 'ERROR') throw new Error(response?.message);
            enqueueSnackbar(response.message, { variant: 'success' });
            navigate('/settings/departments');
        } catch (err) {
            if (err?.statusCode === 400) enqueueSnackbar(err?.data?.message, { variant: 'error' });
            else enqueueSnackbar(err?.message || 'Something went wrong!', { variant: 'error' });
        }
    };

    const formik = useFormik({
        initialValues: isEditMode
            ? {
                  id: results?.id ?? '',
                  name: results?.name ?? '',
                  status: results?.status ?? 'Active',
              }
            : {
                  departments: [{ name: '', status: 'Active' }],
              },
        enableReinitialize: true,
        validationSchema: isEditMode
            ? Yup.object().shape({
                  name: nameSchema,
                  status: statusSchema,
              })
            : Yup.object().shape({
                  departments: Yup.array()
                      .min(1, 'At least one department is required')
                      .max(10, 'Maximum 10 departments allowed at a time')
                      .of(departmentItemSchema),
              }),
        onSubmit: async (values, helpers) => {
            try {
                if (isEditMode) {
                    await handleModifyDepartment({ ...values, name: values.name?.trim().replace(/\s+/g, ' ') });
                } else {
                    const departments = values.departments.map((department) => ({
                        ...department,
                        name: department.name?.trim().replace(/\s+/g, ' '),
                    }));
                    setServerFieldErrors([]);
                    await handleCreateDepartments(departments);
                }
                helpers.setStatus({ success: true });
                helpers.setSubmitting(false);
            } catch (err) {
                console.error(err);
                helpers.setStatus({ success: false });
                helpers.setErrors({
                    submit: err.message,
                });
                helpers.setSubmitting(false);
            }
        },
    });

    if (isLoading) {
        return (
            <Box sx={{ py: 4 }}>
                <Skeleton height={42} />
                <Skeleton />
                <Skeleton />
            </Box>
        );
    }

    return (
        <>
            <Grid container spacing={4} sx={{ py: 4 }}>
                <Grid item md={12} xs={12}>
                    <form onSubmit={formik.handleSubmit}>
                        <div>
                            <Grid container columnSpacing={6} rowSpacing={3}>
                                {isEditMode ? (
                                    <>
                                        <Grid item xs={4}>
                                            <TextField
                                                error={Boolean(formik.touched.name && formik.errors.name)}
                                                fullWidth
                                                helperText={formik.touched.name && formik.errors.name}
                                                label="Name"
                                                name="name"
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                                value={formik.values.name}
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                fullWidth
                                                error={Boolean(formik.touched.status && formik.errors.status)}
                                                label="Status"
                                                name={'status'}
                                                helperText={formik.touched.status && formik.errors.status}
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                                select
                                                children={status.map((status) => (
                                                    <MenuItem key={status} value={status}>
                                                        {status}
                                                    </MenuItem>
                                                ))}
                                                value={formik.values.status}
                                            />
                                        </Grid>
                                    </>
                                ) : (
                                    <DepartmentFields
                                        departments={formik.values.departments}
                                        statusOptions={status}
                                        touched={formik.touched.departments}
                                        errors={formik.errors.departments}
                                        serverFieldErrors={serverFieldErrors}
                                        setDepartments={(values) => {
                                            if (values.length !== formik.values.departments.length) {
                                                setServerFieldErrors([]);
                                            }
                                            formik.setFieldValue('departments', values);
                                        }}
                                        onBlur={formik.setFieldTouched}
                                        onClearServerError={handleClearServerError}
                                    />
                                )}
                                {formik.errors.submit && (
                                    <Grid item xs={12}>
                                        <FormHelperText error>{formik.errors.submit}</FormHelperText>
                                    </Grid>
                                )}
                                {isEditMode && (
                                    <Grid item xs={12} sx={styles.dividerWrapper}>
                                        <Divider flexItem orientation="horizontal" sx={styles.divider} />{' '}
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <Stack direction="row" justifyContent="end">
                                        <LoadingButton
                                            loading={loading}
                                            color="primary"
                                            size="large"
                                            type="submit"
                                            variant="contained"
                                        >
                                            {isEditMode ? 'Update' : 'Create'}
                                        </LoadingButton>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </div>
                    </form>
                </Grid>
            </Grid>
        </>
    );
};

export default DepartmentDetailsTab;
